/**
 * useStatisticsValue Hook
 *
 * Custom React hook for fetching age-specific statistic values
 * from the pre-loaded StatisticsMap. These values are used in MarketStatistic
 * components to show users how their totals compare to Canadian averages.
 */

import { useSettings } from "../contexts/SettingsContext";
import type { AgeStatistics } from "../types/statistics";

/**
 * Field names for each category statistic
 */
const STATISTICS_FIELDS = {
  income: [
    "employmentIncome",
    "pensionIncome",
    "cppIncome",
    "oasIncome",
    "investmentIncome",
    "rentalIncome",
    "otherIncome",
    "bonusVariableIncome",
    "businessIncome",
  ],
  expenses: [
    "housing",
    "utilities",
    "food",
    "transportation",
    "insurance",
    "healthcare",
    "other",
    "personalDiscretionary",
  ],
  investableAssets: [
    "cashSavings",
    "emergencyFund",
    "gicTermDeposits",
    "rrspBalance",
    "spousalRrspBalance",
    "tfsaBalance",
    "fhsaBalance",
    "respBalance",
    "liraBalance",
    "rrifBalance",
    "dpspBalance",
    "nonRegisteredInvestments",
    "dbPensionCommutedValue",
    "dcPensionBalance",
    "groupRrspBalance",
    "stockOptionsRsusValue",
  ],
  realEstateAssets: [
    "primaryResidence",
    "vacationProperty",
    "investmentProperties",
    "landOtherRealEstate",
    "businessValue",
    "shareholderLoansReceivable",
    "insuranceCashValue",
    "otherAssets",
    "depreciatingAssets",
  ],
  liabilities: [
    "mortgage",
    "creditCardBalance",
    "carLoan",
    "lineOfCredit",
    "studentLoans",
    "otherLiabilities",
    "investmentPropertyMortgage",
    "heloc",
    "businessLoans",
    "taxesPayable",
  ],
} as const;

export type StatisticsCategory = keyof typeof STATISTICS_FIELDS;

/**
 * Field filter function type - determines if a field should be included
 */
export type FieldFilter = (fieldName: string) => boolean;

/**
 * Calculate statistic value for a category from a pre-loaded AgeStatistics map.
 * Silently skips fields not present in the map (the map may be empty before
 * statistics finish loading, or a test may provide a partial map).
 */
function calculateStatisticFromData(
  category: StatisticsCategory,
  statsMap: AgeStatistics,
  fieldFilter?: FieldFilter
): number {
  const fieldNames = STATISTICS_FIELDS[category];
  let total = 0;

  for (const fieldName of fieldNames) {
    if (fieldFilter && !fieldFilter(fieldName)) continue;

    const stat = statsMap[fieldName];
    if (!stat) continue;

    total += stat.average;
  }

  return total;
}

/**
 * Hook to get age-specific statistic value for a category.
 */
export function useStatisticsValue(
  category: StatisticsCategory,
  fieldFilter?: FieldFilter
): number {
  const { statisticsMap } = useSettings();

  return Math.round(
    calculateStatisticFromData(category, statisticsMap, fieldFilter)
  );
}
